import React, { useState, useEffect, useRef } from 'react';
import { 
  openMediaStreamCamera, 
  capturePhotoFromStream, 
  stopCameraStream,
  getAvailableCameras 
} from '../../utils/cameraUtils';
import './CameraModal.css';

/**
 * CameraModal Component
 * Full-screen camera interface using MediaStream API
 * Provides instant photo capture without native OS confirmation dialogs
 * 
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onCapture - Callback when photo is captured (receives File object)
 * @param {function} onClose - Callback when modal is closed
 * @param {string} initialFacingMode - 'environment' (rear) or 'user' (front) camera
 */
function CameraModal({ 
  isOpen, 
  onCapture, 
  onClose, 
  initialFacingMode = 'environment' 
}) {
  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState(initialFacingMode);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const videoRef = useRef(null);

  // Check for multiple cameras on mount
  useEffect(() => {
    const checkCameras = async () => {
      try {
        const cameras = await getAvailableCameras();
        setHasMultipleCameras(cameras.length > 1);
      } catch (err) {
        console.warn('[CameraModal] Could not enumerate cameras:', err);
      }
    };
    
    if (isOpen) {
      checkCameras();
    }
  }, [isOpen]);

  // Initialize camera when modal opens or facingMode changes
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let mounted = true;

    const startCamera = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log('[CameraModal] Starting camera with facingMode:', facingMode);
        
        // Request camera access with specified facing mode
        const mediaStream = await openMediaStreamCamera(facingMode);
        
        if (!mounted) {
          // Component unmounted while waiting for camera
          stopCameraStream(mediaStream);
          return;
        }

        // Set stream to video element
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          
          // Wait for video to be ready
          await new Promise((resolve, reject) => {
            if (!videoRef.current) {
              reject(new Error('Video element not found'));
              return;
            }
            
            videoRef.current.onloadedmetadata = () => {
              if (videoRef.current) {
                videoRef.current.play()
                  .then(resolve)
                  .catch(reject);
              }
            };
            
            // Timeout after 5 seconds
            setTimeout(() => reject(new Error('Camera initialization timeout')), 5000);
          });
        }

        setStream(mediaStream);
        setLoading(false);
        console.log('[CameraModal] Camera started successfully');
      } catch (err) {
        console.error('[CameraModal] Failed to start camera:', err);
        
        if (!mounted) return;

        // Set user-friendly error message
        let errorMessage = 'Unable to access camera';
        
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          errorMessage = 'Camera permission denied. Please enable camera access in your browser settings.';
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          errorMessage = 'No camera found on this device.';
        } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
          errorMessage = 'Camera is already in use by another application.';
        } else if (err.name === 'OverconstrainedError') {
          errorMessage = 'Camera does not support the requested settings.';
        } else if (err.message) {
          errorMessage = err.message;
        }

        setError(errorMessage);
        setLoading(false);
      }
    };

    startCamera();

    // Cleanup function
    return () => {
      mounted = false;
      if (stream) {
        console.log('[CameraModal] Cleaning up camera stream');
        stopCameraStream(stream);
      }
    };
  }, [isOpen, facingMode]);

  // Handle photo capture
  const handleCapture = async () => {
    if (capturing || !videoRef.current || !stream) {
      return;
    }

    setCapturing(true);

    try {
      console.log('[CameraModal] Capturing photo from stream');
      
      // Capture photo from video stream
      const photoFile = await capturePhotoFromStream(videoRef.current);
      
      console.log('[CameraModal] Photo captured:', {
        filename: photoFile.name,
        size: photoFile.size,
        type: photoFile.type
      });

      // Call the onCapture callback with the photo file
      if (onCapture) {
        await onCapture(photoFile);
      }

      // Close the modal
      handleClose();
    } catch (err) {
      console.error('[CameraModal] Failed to capture photo:', err);
      setError('Failed to capture photo. Please try again.');
      setCapturing(false);
    }
  };

  // Handle camera switch (front/rear)
  const handleSwitchCamera = () => {
    console.log('[CameraModal] Switching camera');
    
    // Toggle facing mode
    const newFacingMode = facingMode === 'environment' ? 'user' : 'environment';
    
    // Clean up current stream
    if (stream) {
      stopCameraStream(stream);
      setStream(null);
    }
    
    // Set new facing mode (will trigger useEffect to start new stream)
    setFacingMode(newFacingMode);
  };

  // Handle close
  const handleClose = () => {
    console.log('[CameraModal] Closing camera modal');
    
    // Clean up stream
    if (stream) {
      stopCameraStream(stream);
      setStream(null);
    }
    
    // Call onClose callback
    if (onClose) {
      onClose();
    }
  };

  // Handle retry after error
  const handleRetry = () => {
    setError(null);
    setLoading(true);
    
    // Force re-initialization by toggling state
    const currentMode = facingMode;
    setFacingMode(currentMode === 'environment' ? 'user' : 'environment');
    setTimeout(() => setFacingMode(currentMode), 50);
  };

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  return (
    <div className="camera-modal" role="dialog" aria-modal="true" aria-label="Camera">
      {/* Video Preview */}
      <video
        ref={videoRef}
        className="camera-preview"
        autoPlay
        playsInline
        muted
        aria-label="Camera preview"
      />

      {/* Loading State */}
      {loading && (
        <div className="camera-loading">
          <div className="loading-spinner" />
          <p>Initializing camera...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="camera-error">
          <div className="error-icon">⚠️</div>
          <p className="error-message">{error}</p>
          <div className="error-actions">
            <button 
              className="btn-retry"
              onClick={handleRetry}
              aria-label="Retry camera access"
            >
              Try Again
            </button>
            <button 
              className="btn-close-error"
              onClick={handleClose}
              aria-label="Close"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Camera Controls - Only show when camera is active */}
      {!loading && !error && stream && (
        <>
          {/* Close Button - Top Left */}
          <button
            className="camera-btn camera-close"
            onClick={handleClose}
            aria-label="Close camera"
            disabled={capturing}
          >
            <span className="btn-icon">✕</span>
          </button>

          {/* Switch Camera Button - Top Right */}
          {hasMultipleCameras && (
            <button
              className="camera-btn camera-switch"
              onClick={handleSwitchCamera}
              aria-label="Switch camera"
              disabled={capturing}
            >
              <span className="btn-icon">🔄</span>
            </button>
          )}

          {/* Capture Button - Bottom Center */}
          <button
            className={`camera-capture ${capturing ? 'capturing' : ''}`}
            onClick={handleCapture}
            aria-label="Take photo"
            disabled={capturing}
          >
            <span className="capture-inner" />
          </button>

          {/* Camera Info - Top Center */}
          <div className="camera-info">
            <span className="camera-mode-indicator">
              {facingMode === 'environment' ? '📷 Rear Camera' : '🤳 Front Camera'}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

export default CameraModal;

